"use strict";
import { useState } from "react";

import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { CallCenterUserCredentialsState, ProviderTypes } from "./CallCenterSettings.types";

import styles from "./CallCenterSettings.module.scss";

interface EditCallCenterComUnikCredentialsProps {
    credentials: CallCenterUserCredentialsState;
    disabled: boolean
    onSave: (credentials: CallCenterUserCredentialsState) => void
    onCancel: () => void
}

function EditCallCenterComUnikCredentials(props: Readonly<EditCallCenterComUnikCredentialsProps>): JSX.Element {
    const { getText } = useTranslation();
    const cancelLabel = getText("CancelLabel");
    const saveLabel = getText("SaveButton");
    const usernameLabel = getText("CallCenterComUnikUsernameLabel");
    const passwordLabel = getText("CallCenterComUnikPasswordLabel");

    const [username, setUsername] = useState(props.credentials?.ExternalUsername ?? null);
    const [password, setPassword] = useState(props.credentials?.Secret ?? null);

    const disabled = props.disabled;

    const isExistingUsername = username === props.credentials?.ExternalUsername;
    const isDifferent = props.credentials?.ExternalUsername !== username || props.credentials?.Secret !== password;

    const isUsernameRequired = password != null;
    const isPasswordRequired = username != null && !isExistingUsername;

    const isUsernameValidEmail = username == null || ValidationHelper.isExactMatch(username, ValidationHelper.emailRegex);
    const isUsernameValidRequired = !isUsernameRequired || username != null;
    const isPasswordValidRequired = !isPasswordRequired || password != null;

    const usernameError = !isUsernameValidEmail ? cacheStore.GetString("EmailHasInvalidFormatLabel") : null;

    const isUsernameValid = isUsernameValidRequired && isUsernameValidEmail;
    const isPasswordValid = isPasswordValidRequired;

    const canSave = isUsernameValid && isPasswordValid && isDifferent;

    const showExistingPasswordPlaceholder = isExistingUsername;

    function trimTextOrNull(text: string): string | null {
        text = text.trim();
        if (text === "") {
            return null;
        }
        return text;
    }

    function onUsernameChanged(text: string): void {
        text = trimTextOrNull(text);
        if (text === username) {
            return;
        }
        setUsername(text);
    }

    function onPasswordChanged(text: string): void {
        text = trimTextOrNull(text);
        if (text === password) {
            return;
        }
        setPassword(text);
    }

    function onSave(): void {
        if (!canSave)
            return;

        let newCredentials = null;
        if (username != null) {
            newCredentials = new CallCenterUserCredentialsState({
                Provider: ProviderTypes.ComUnik,
                ExternalUsername: username,
                Secret: password
            });
        }
        props.onSave(newCredentials);
    }

    function onCancel(): void {
        props.onCancel();
    }

    return (
        <div className={styles.editCredentials}>
            <div className={styles.textbox}>
                <label htmlFor="comUnikUsername">{usernameLabel}</label>
                <TextboxControl
                    id="comUnikUsername"
                    dataCy="CallCenterUsername"
                    type="email"
                    value={username}
                    valid={isUsernameValid}
                    onChange={onUsernameChanged}
                    disabled={disabled}
                    maxLength={200}
                    style={{ height: "37px" }}
                />
                <small
                    className={styles.errorMessage}
                    style={{ visibility: usernameError == null ? "hidden" : "visible" }}
                >
                    {usernameError ?? "&nbsp;"}
                </small>
            </div>
            <div className={styles.textbox}>
                <label htmlFor="callCenterPassword">{passwordLabel}</label>
                <TextboxControl
                    id="callCenterPassword"
                    dataCy="CallCenterPassword"
                    type="password"
                    value={password}
                    valid={isPasswordValid}
                    onChange={onPasswordChanged}
                    disabled={disabled}
                    placeholder={showExistingPasswordPlaceholder ? "••••••••" : ""}
                    style={{ height: "37px" }}
                />
                <small style={{ visibility: "hidden" }}>&nbsp;</small>
            </div>
            <div>
                <label style={{ visibility: "hidden" }}>&nbsp;</label>
                <div className={styles.saveClose}>
                    <button
                        disabled={props.disabled}
                        onClick={onCancel}
                        className="btn btn-default"
                        data-cy="CallCenterCancelCredentials"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        disabled={props.disabled || !(canSave)}
                        onClick={onSave}
                        className="btn btn-primary"
                        data-cy="CallCenterSaveCredentials"
                    >
                        {saveLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { EditCallCenterComUnikCredentials };
